import { observable } from 'mobx';
import { MarketStore } from '.';
import { IDisposable } from '../interface';
import { Depth, DepthItem, DepthLevel, DepthType } from '../canister/Depth';
import { Fusion, SpotOrderType } from '../canister/Fusion';
import {
  DepthDirection,
  SwapDepth,
  SwapDepthItem
} from '../utils/helper/converter/math';
import { toBigInt, toBigNumber, zero } from '../utils/helper';
import {
  SWAP_DEPTH_PRICE_FACTOR,
  SWAP_DEPTH_VOLUME_FACTOR
} from '../utils/config';
import BigNumber from 'bignumber.js';

export class DepthStore implements IDisposable {
  @observable
  public type: DepthType = DepthType.All;

  @observable
  public level: DepthLevel = DepthLevel.One;

  @observable
  public count: number = 10;

  @observable
  public bidOrders: Array<DepthItem> = [];

  @observable
  askOrders: Array<DepthItem> = [];

  private timer?: NodeJS.Timer = undefined;
  private fetchTimeInterval: number = 1000 * 10;
  private marketStore: MarketStore;
  private depthActor: Depth;
  private fusionActor: Fusion;

  constructor(marketStore: MarketStore) {
    this.marketStore = marketStore;
    this.level = this.marketStore.marketSetting.defaultDepthLevel;
    this.depthActor = new Depth(
      this.marketStore.baseToken.decimals,
      this.marketStore.marketSetting
    );
    this.fusionActor = new Fusion(
      this.marketStore.marketSetting.marketId,
      this.marketStore.marketSetting.priceDecimals
    );
    this.fetchDepth();
    this.startFetchTimer();
  }

  /**
   * switch depth type
   * @param type
   */
  switchType = (type: DepthType) => {
    if (!type) return;
    this.type = type;
    switch (type) {
      case DepthType.Bid:
        this.count = 20;
        break;
      case DepthType.Ask:
        this.count = 20;
        break;
      case DepthType.All:
        this.count = 10;
        break;
      default:
        this.count = 10;
        break;
    }

    console.debug(
      `Depth.switchType: switch type to ${type.toString()} , 
            depth count ${this.count}`
    );
    this.fetchDepth();
    this.restartFetchTimer();
  };

  /**
   * switch depth level
   * @param newLevel
   */
  switchLevel = (newLevel: DepthLevel) => {
    if (!newLevel) return;

    this.level = DepthLevel[newLevel.toString()];
    this.fetchDepth();
    this.restartFetchTimer();
    console.debug(`Depth.switchLevel: switch level to ${this.level}  `);
  };

  private startFetchTimer = () => {
    this.timer = setInterval(this.fetchDepth, this.fetchTimeInterval);
  };

  private stopFetchTimer = () => {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  };

  private restartFetchTimer = () => {
    this.stopFetchTimer();
    this.startFetchTimer();
  };

  /**
   * fetch depth data
   */
  private fetchDepth = async () => {
    const { baseToken, quoteToken, marketSetting } = this.marketStore;
    const depthTask = this.depthActor.getDepth(this.level, this.count);
    const swapTask = this.fusionActor.fetchLiquidityInfo();

    const [depth, swap] = await Promise.all([depthTask, swapTask]);
    console.debug(`Depth.fetchDepth: swap liquidity ${JSON.stringify(swap)}`);
    const swapDepth = new SwapDepth({
      liquidity: toBigInt(swap.poolLP),
      volume: toBigInt(swap.poolVolume),
      amount: toBigInt(swap.poolAmount),
      decimals: {
        volume: baseToken.decimals,
        amount: quoteToken.decimals,
        price: marketSetting.priceDecimals
      },
      priceFixed: marketSetting.orderPricePrecision
    });

    const miniPrice = marketSetting.depthLevels[this.level - 1];
    const miniVolume = toBigNumber(1)
      .shiftedBy(-marketSetting.depthVolumePrecision)
      .times(SWAP_DEPTH_VOLUME_FACTOR);

    const bidOrderOption = {
      direction: DepthDirection.Bid,
      deltaP: toBigNumber(miniPrice).times(SWAP_DEPTH_PRICE_FACTOR),
      maxCount: this.count,
      minVolume: miniVolume,

      latestPrice: toBigNumber(this.marketStore?.latestPrice?.price || 0)
    };
    console.debug(
      `Depth.fetchDepth: bidOrderOption ${JSON.stringify(bidOrderOption)}`
    );
    const mapBidsOrders = swapDepth.getDepth(bidOrderOption).depthItems;
    console.debug(`Depth.fetchDepth: mapBidsOrders ${mapBidsOrders.length}`);
    const askOrderOption = {
      direction: DepthDirection.Ask,
      deltaP: toBigNumber(miniPrice).times(SWAP_DEPTH_PRICE_FACTOR),
      maxCount: this.count,
      minVolume: miniVolume,
      latestPrice: toBigNumber(this.marketStore?.latestPrice?.price || 0)
    };
    console.debug(
      `Depth.fetchDepth: askOrderOption ${JSON.stringify(askOrderOption)}`
    );
    const mapAsksOrders = swapDepth.getDepth(askOrderOption).depthItems;
    console.debug(`Depth.fetchDepth: mapAsksOrders ${mapAsksOrders.length}`);

    this.bidOrders = this.mergeOrders(
      depth.bids,
      mapBidsOrders,
      SpotOrderType.Bid
    );
    this.askOrders = this.mergeOrders(
      depth.asks,
      mapAsksOrders,
      SpotOrderType.Ask
    );

    console.debug(`Depth.fetchDepth: fetch depth success`);
  };

  private mergeOrders = (
    ordersA: Array<DepthItem>,
    ordersB: Array<DepthItem>,
    type: SpotOrderType
  ) => {
    const orders = ordersA.concat(ordersB);
    const map = new Map<string, DepthItem>();
    orders.forEach(order => {
      const key = order.price.toString();
      if (map.has(key)) {
        const oldOrder = map.get(key);
        oldOrder.volume = oldOrder.volume.plus(order.volume);
      } else {
        map.set(key, order);
      }
    });
    const returnOrders = Array.from(map.values());
    const returnCount = Math.min(returnOrders.length, this.count);
    switch (type) {
      case SpotOrderType.Bid: {
        return returnOrders
          .sort((a, b) => b.price.comparedTo(a.price))
          .slice(0, returnCount);
      }
      case SpotOrderType.Ask:
        return returnOrders
          .sort((a, b) => a.price.comparedTo(b.price))
          .slice(0, returnCount);
    }
  };

  /**
   * must be called before destroy,
   * otherwise timer will not be cleared
   */
  dispose = () => this.stopFetchTimer;
}
